// src/App.tsx
import React from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import LoginPage from "./pages/LoginPage";
import CaseDetailPage from "./pages/CaseDetailPage/CaseDetailPage";
import CaseListPage from "./pages/CaseListPage/CaseListPage";
import SupervisorListPage from "./pages/SupervisorListPage/SupervisorListPage";
import AccountPage from "./pages/Settings/SettingsPage";
import { AuthProvider } from "./contexts/AuthContext";
import { CaseProvider } from "./contexts/CaseContext";
import ProtectedRoute from "./components/ProtectedRoute";
import SupervisorDetailPage from "./pages/SupervisorDetailPage/SupervisorDetailPage";
import { SupervisorProvider } from "./contexts/SupervisorContext";
import { AccountProvider } from "./contexts/AccountContext";
import { VisitProvider } from "./contexts/VisitContext";
import { LocationProvider } from "./contexts/LocationContext";
import UnProtectedRoute from "./components/UnprotectRoute";
import { UserProvider } from "./contexts/UserContext";
import { ChildProvider } from "./contexts/ChildContext";
import { ReportProvider } from "./contexts/ReportContext";
import { DashboardProvider } from "./contexts/DashboardContext";
import { ContractProvider } from "./contexts/ContractContext";
import { ParentProvider } from "./contexts/ParentContext";
import { AttorneyProvider } from "./contexts/AttorneyContext";
import ParentListPage from "./pages/ParentListPage/ParentListPage";
import ParentDetailPage from "./pages/ParentDetailPage/ParentDetailPage";
import AttorneyListPage from "./pages/AttorneyListPage/AttorneyListPage";
import AttorneyDetailPage from "./pages/AttorneyDetailPage/AttorneyDetailPage";
import OnboardingPage from "./pages/Onboarding/OnboardingPage";
import BusinessStep from "./pages/Onboarding/components/BusinessStep";
import BusinessAddressStep from "./pages/Onboarding/components/BusinessAddressStep";
import OrganizationStep from "./pages/Onboarding/components/OrganizationStep";
import CalendarStep from "./pages/Onboarding/components/CalendarStep";
import StripeStep from "./pages/Onboarding/components/StripeStep";
import ChildDetailPage from "./pages/ChildDetailPage/ChildDetailPage";
import ChildListPage from "./pages/ChildListPage/ChildListPage";
import ReportDetailPage from "./pages/ReportDetailPage/ReportDetailPage";
import { ProductProvider } from "./contexts/ProductContext";
import { ContactProvider } from "./contexts/ContactContext";
import SchedulerPage from "./pages/Scheduler/SchedulerPager";
import CallbackPage from "./pages/Callback/CallbackPage";
import { BusinessProvider } from "./contexts/BusinessContext";
import RegisterPage from "./pages/RegisterPage";
import SignupPage from "./pages/SignupPage";
import JoinOrganizationPage from "./pages/JoinOrganizationPage";
import WelcomePage from "./pages/Welcome/WelcomePage";
import { ConversationProvider } from "./contexts/ConversationContext";
import { OnboardingProvider } from "./contexts/Onboarding";
import { DocumentTemplateProvider } from "./contexts/DocumentTemplateContext";
import { DocumentProvider } from "./contexts/DocumentContext";
import { APIProvider } from "@vis.gl/react-google-maps";
import { SchedulerProvider } from "./contexts/SchedulerContext";
import ForgotPasswordPage from "./pages/ForgotPassword";
import { InquiryProvider } from "./contexts/InquiryContext";
import DemographicsPage from "./pages/DemographicsPage/DemographicsPage";
import TeamPage from "./pages/TeamPage";
import { CreateVisitDialogProvider } from "./providers/CreateVisitDialogProvider";
import { LocationDialogProvider } from "./providers/LocationDialogProvider";
import { AttorneyDialogProvider } from "./providers/AttorneyDialogProvider";
import { DeleteAlertProvider } from "./contexts/DeleteAlertProvider";
import { ThemeProvider } from "./contexts/ThemeContext";
import { MonitorProvider } from "./contexts/MonitorContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { SubscriptionGate } from "./components/SubscriptionGate";
import PhoneLoginPage from "./pages/PhoneLoginPage";
import RecaptchaBadgeVisibility from "./components/RecaptchaBadgeVisibility";
import TokenLoginPage from "./pages/TokenLoginPage";
import AuthActionHandler from "./pages/AuthActionHandler";
import InvitationResponsePage from "./pages/InvitationResponsePage";
import MessagesPage from "./pages/MessagesPage/MessagesPage";
import SurveyPage from "./pages/SurveyPage/SurveyPage";

// Role-based components
import RoleBasedLayout from "./components/RoleBasedLayout";
import RoleBasedProtectedRoute from "./components/RoleBasedProtectedRoute";
import RoleBasedDashboard from "./pages/Dashboard/RoleBasedDashboard";
import RoleBasedVisits from "./pages/Visits/RoleBasedVisits";
import RoleBasedReports from "./pages/Reports/RoleBasedReports";
import RoleBasedProfile from "./pages/Profile/RoleBasedProfile";
import RoleBasedVisitDetail from "./pages/Visits/RoleBasedVisitDetail";
import LoadingView from "./components/LoadingView";
import { useAccount } from "./contexts/AccountContext";

/**
 * Inner providers that depend on account being loaded.
 * Wait for account loading to finish before rendering child providers.
 * Note: account may be null for some users (e.g., if claims don't include readAccountIds),
 * but we still render the providers - they should handle null account gracefully.
 */
const AccountDependentProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loading } = useAccount();

  // Wait for account loading to finish (but account may still be null)
  if (loading) {
    return <LoadingView />;
  }

  return (
    <SubscriptionProvider>
      <SubscriptionGate>
        <DashboardProvider>
          <CaseProvider>
            <SupervisorProvider>
              <VisitProvider>
                <SchedulerProvider>
                  <LocationProvider>
                    <UserProvider>
                      <ChildProvider>
                        <ReportProvider>
                          <ContractProvider>
                            <ParentProvider>
                              <AttorneyProvider>
                                <ProductProvider>
                                  <ContactProvider>
                                    <ConversationProvider>
                                      <DocumentProvider>
                                        <DocumentTemplateProvider>
                                          <InquiryProvider>
                                            <CreateVisitDialogProvider>
                                              <LocationDialogProvider>
                                                <AttorneyDialogProvider>
                                                  <DeleteAlertProvider>
                                                    {children}
                                                  </DeleteAlertProvider>
                                                </AttorneyDialogProvider>
                                              </LocationDialogProvider>
                                            </CreateVisitDialogProvider>
                                          </InquiryProvider>
                                        </DocumentTemplateProvider>
                                      </DocumentProvider>
                                    </ConversationProvider>
                                  </ContactProvider>
                                </ProductProvider>
                              </AttorneyProvider>
                            </ParentProvider>
                          </ContractProvider>
                        </ReportProvider>
                      </ChildProvider>
                    </UserProvider>
                  </LocationProvider>
                </SchedulerProvider>
              </VisitProvider>
            </SupervisorProvider>
          </CaseProvider>
        </DashboardProvider>
      </SubscriptionGate>
    </SubscriptionProvider>
  );
};

/**
 * Wrapper component that provides all the necessary contexts for authenticated routes.
 * This is shared across all roles, with role-specific features handled by the pages themselves.
 */
const AuthenticatedProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <AccountProvider>
      <OnboardingProvider>
        <MonitorProvider>
          <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? ""}>
            <AccountDependentProviders>
              {children}
            </AccountDependentProviders>
          </APIProvider>
        </MonitorProvider>
      </OnboardingProvider>
    </AccountProvider>
  );
};

/**
 * Redirects a legacy route to a new path, interpolating a single route param.
 * <Navigate to="/visit/:visitId" /> does NOT interpolate params, so the raw
 * ":visitId" would end up in the URL. This reads the param and navigates to the
 * concrete path instead.
 */
const ParamRedirect: React.FC<{ param: string; to: (value: string) => string }> = ({
  param,
  to,
}) => {
  const params = useParams();
  const value = params[param];
  return <Navigate to={value ? to(value) : "/dashboard"} replace />;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <div className="antialiased">
        <ThemeProvider>
          <AuthProvider>
          <Router>
            <RecaptchaBadgeVisibility />
            <Routes>
              {/* Fully public routes (no auth wrapper) */}
              <Route path="/invitations/:visitId" element={<InvitationResponsePage />} />

              {/* Survey response - staff only (owner/admin/supervisor). Requires
                  auth; the `u` query param in email links is informational only. */}
              <Route
                path="/survey/:surveyId"
                element={
                  <RoleBasedProtectedRoute allowedRoles={["owner", "admin", "supervisor"]}>
                    <SurveyPage />
                  </RoleBasedProtectedRoute>
                }
              />

              {/* Public routes */}
              <Route element={<UnProtectedRoute />}>
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/phone-login" element={<PhoneLoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/signup/join" element={<JoinOrganizationPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/token-login" element={<TokenLoginPage />} />
                <Route path="/auth/action" element={<AuthActionHandler />} />
              </Route>

              {/* Welcome page - checks for pending invites after registration */}
              <Route
                path="/welcome"
                element={<WelcomePage />}
              />

              {/* Onboarding - admin only */}
              <Route
                path="/onboarding"
                element={
                  <AccountProvider>
                    <SupervisorProvider>
                      <BusinessProvider>
                        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? ""}>
                          <ProtectedRoute>
                            <OnboardingPage />
                          </ProtectedRoute>
                        </APIProvider>
                      </BusinessProvider>
                    </SupervisorProvider>
                  </AccountProvider>
                }
              >
                <Route index element={<BusinessStep />} />
                <Route path="address" element={<BusinessAddressStep />} />
                <Route path="organization" element={<OrganizationStep />} />
                <Route path="calendar" element={<CalendarStep />} />
                <Route path="subscription" element={<StripeStep />} />
              </Route>

              {/* Main authenticated routes with role-based layout */}
              <Route
                path="*"
                element={
                  <AuthenticatedProviders>
                    <RoleBasedProtectedRoute>
                      <RoleBasedLayout>
                        <Routes>
                          {/* Shared routes - content varies by role */}
                          <Route path="/dashboard" element={<RoleBasedDashboard />} />
                          <Route path="/visits" element={<RoleBasedVisits />} />
                          <Route path="/messages" element={<MessagesPage />} />
                          <Route path="/reports" element={<RoleBasedReports />} />
                          <Route path="/profile" element={<RoleBasedProfile />} />

                          {/* Visit detail - uses different component based on role */}
                          <Route path="/visit/:visitId" element={<RoleBasedVisitDetail />} />

                          {/* Report detail - shared across roles */}
                          <Route path="/report/:reportId" element={<ReportDetailPage />} />

                          {/* Admin-only routes */}
                          <Route
                            path="/scheduler"
                            element={
                              <RoleBasedProtectedRoute allowedRoles={["owner", "admin"]}>
                                <SchedulerPage />
                              </RoleBasedProtectedRoute>
                            }
                          />
                          <Route
                            path="/cases"
                            element={
                              <RoleBasedProtectedRoute allowedRoles={["owner", "admin"]}>
                                <CaseListPage />
                              </RoleBasedProtectedRoute>
                            }
                          />
                          <Route
                            path="/case/:caseId"
                            element={
                              <RoleBasedProtectedRoute allowedRoles={["owner", "admin"]}>
                                <CaseDetailPage />
                              </RoleBasedProtectedRoute>
                            }
                          />
                          <Route
                            path="/supervisors"
                            element={
                              <RoleBasedProtectedRoute allowedRoles={["owner", "admin"]}>
                                <SupervisorListPage />
                              </RoleBasedProtectedRoute>
                            }
                          />
                          <Route
                            path="/supervisor/:supervisorId"
                            element={
                              <RoleBasedProtectedRoute allowedRoles={["owner", "admin"]}>
                                <SupervisorDetailPage />
                              </RoleBasedProtectedRoute>
                            }
                          />
                          <Route
                            path="/parents"
                            element={
                              <RoleBasedProtectedRoute allowedRoles={["owner", "admin"]}>
                                <ParentListPage />
                              </RoleBasedProtectedRoute>
                            }
                          />
                          <Route
                            path="/parent/:parentId"
                            element={
                              <RoleBasedProtectedRoute allowedRoles={["owner", "admin"]}>
                                <ParentDetailPage />
                              </RoleBasedProtectedRoute>
                            }
                          />
                          <Route
                            path="/children"
                            element={
                              <RoleBasedProtectedRoute allowedRoles={["owner", "admin"]}>
                                <ChildListPage />
                              </RoleBasedProtectedRoute>
                            }
                          />
                          <Route
                            path="/child/:childId"
                            element={
                              <RoleBasedProtectedRoute allowedRoles={["owner", "admin"]}>
                                <ChildDetailPage />
                              </RoleBasedProtectedRoute>
                            }
                          />
                          <Route
                            path="/attorneys"
                            element={
                              <RoleBasedProtectedRoute allowedRoles={["owner", "admin"]}>
                                <AttorneyListPage />
                              </RoleBasedProtectedRoute>
                            }
                          />
                          <Route
                            path="/attorney/:attorneyId"
                            element={
                              <RoleBasedProtectedRoute allowedRoles={["owner", "admin"]}>
                                <AttorneyDetailPage />
                              </RoleBasedProtectedRoute>
                            }
                          />
                          <Route
                            path="/demographics"
                            element={
                              <RoleBasedProtectedRoute allowedRoles={["owner", "admin"]}>
                                <DemographicsPage />
                              </RoleBasedProtectedRoute>
                            }
                          />
                          <Route
                            path="/settings/*"
                            element={
                              <RoleBasedProtectedRoute allowedRoles={["owner", "admin"]}>
                                <AccountPage />
                              </RoleBasedProtectedRoute>
                            }
                          />
                          <Route
                            path="/callback/:type"
                            element={
                              <RoleBasedProtectedRoute allowedRoles={["owner", "admin"]}>
                                <CallbackPage />
                              </RoleBasedProtectedRoute>
                            }
                          />
                          <Route
                            path="/team"
                            element={
                              <RoleBasedProtectedRoute allowedRoles={["owner", "admin"]}>
                                <TeamPage />
                              </RoleBasedProtectedRoute>
                            }
                          />

                          {/* Legacy /monitor routes - redirect to new paths */}
                          <Route path="/monitor" element={<Navigate to="/dashboard" replace />} />
                          <Route path="/monitor/visits" element={<Navigate to="/visits" replace />} />
                          <Route path="/monitor/visit/:visitId" element={<ParamRedirect param="visitId" to={(v) => `/visit/${v}`} />} />
                          <Route path="/monitor/reports" element={<Navigate to="/reports" replace />} />
                          <Route path="/monitor/report/:reportId" element={<ParamRedirect param="reportId" to={(v) => `/report/${v}`} />} />
                          <Route path="/monitor/profile" element={<Navigate to="/profile" replace />} />

                          {/* Redirect any unknown routes to dashboard */}
                          <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                      </RoleBasedLayout>
                    </RoleBasedProtectedRoute>
                  </AuthenticatedProviders>
                }
              />
            </Routes>
          </Router>
        </AuthProvider>
      </ThemeProvider>
      </div>
    </ErrorBoundary>
  );
};

export default App;
